// src/petaEsok/PetaEsokModal.tsx
//
// BekalNanti.ID — Milestone P7: PetaEsok Core Trajectory Experience
// Authority: BEKALNANTI_PetaEsok_Experience_Spec_v1.0.md & P7-A Contract
//
// "The system calculates (P5). PetaEsok maps (P7). AI explains. Life changes. BekalNanti re-maps."
// "BekalNanti adalah rumahnya. PetaEsok adalah pengalaman perencanaannya."
//
// Answers:
// 1. Where am I? (Current Reality)
// 2. Where am I going? (Target Destination & Horizon)
// 3. The Journey (Deterministic Timeline Milestones)
// 4. Reality Check (Preserving P6 Status & Semantics)
// 5. Bekal Layer (Confirmed vs Estimated vs Unknown distinction)
// 6. Pressure Points & Known Unknowns
// 7. Legitimate Routes (User chooses, no auto-optimization)
// 8. Honest Non-Education State

import React, { useState } from 'react'
import { GoalRecord, JourneyData } from '../journey/types'
import { buildPetaEsokTrajectory } from '../lib/petaEsokService'
import { PetaEsokRouteId } from '../lib/petaEsokTypes'
import { formatIDR } from '../lib/financialCalculation'
import { deriveTrajectoryActions } from '../lib/actionService'
import { simulateWhatIfScenario } from '../lib/whatIfService'
import { WhatIfScenarioModifier, WhatIfComparisonResult } from '../lib/whatIfTypes'
import { createPlanSnapshot, getPlanSnapshots } from '../lib/snapshotService'
import { PlanSnapshotRecord } from '../lib/snapshotTypes'
import { executeResearchQuery, acceptResearchCandidate, rejectResearchCandidate } from '../lib/researchService'
import { ResearchQueryResult } from '../lib/researchTypes'
import {
  Compass,
  X,
  Calendar,
  Clock,
  ShieldCheck,
  AlertTriangle,
  AlertCircle,
  HelpCircle,
  CheckCircle2,
  TrendingUp,
  MapPin,
  Route,
  Sparkles,
  ChevronRight,
  Wallet,
  ArrowRight,
  ListTodo,
  SlidersHorizontal,
  History,
  Search,
  Check,
  RotateCcw,
} from 'lucide-react'

interface PetaEsokModalProps {
  isOpen: boolean
  goal: GoalRecord | null | undefined
  journey: JourneyData
  onClose: () => void
  onOpenRealityCheck?: (goalId?: string) => void
  onOpenCalculation?: (goalId?: string) => void
  onOpenCostFunding?: () => void
}

export const PetaEsokModal: React.FC<PetaEsokModalProps> = ({
  isOpen,
  goal,
  journey,
  onClose,
  onOpenRealityCheck,
  onOpenCalculation,
  onOpenCostFunding,
}) => {
  const [selectedRouteId, setSelectedRouteId] = useState<PetaEsokRouteId>('primary')
  const [activeTab, setActiveTab] = useState<'journey' | 'bekal' | 'routes' | 'uncertainties' | 'actions' | 'whatif' | 'research' | 'snapshots'>('journey')

  // P8 Actions state
  const [completedActionIds, setCompletedActionIds] = useState<string[]>([])

  // P9 What-If state
  const [whatIfModifier, setWhatIfModifier] = useState<WhatIfScenarioModifier>({
    tuitionAdjustmentPercent: 0,
    livingCostAdjustmentPercent: 0,
    additionalMonthlyContribution: 0,
    additionalOneTimeFunding: 0,
  })
  const [whatIfResult, setWhatIfResult] = useState<WhatIfComparisonResult | null>(null)

  // P10 Snapshot state
  const [snapshots, setSnapshots] = useState<PlanSnapshotRecord[]>(() => (goal ? getPlanSnapshots(goal.id) : []))
  const [snapshotSuccessNote, setSnapshotSuccessNote] = useState<string | null>(null)

  // P11 Research state
  const [researchState, setResearchState] = useState<ResearchQueryResult | null>(null)
  const [isResearchLoading, setIsResearchLoading] = useState(false)
  const [researchMode, setResearchMode] = useState<'normal' | 'conflict' | 'fail'>('normal')

  if (!isOpen || !goal) return null

  // Build the deterministic PetaEsok trajectory map (Consuming canonical P5 & P6)
  const trajectory = buildPetaEsokTrajectory(goal, journey, selectedRouteId)

  // Reality Check status styling badge
  const renderStatusBadge = () => {
    switch (trajectory.realityCheckStatus) {
      case 'fully_funded':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle2 size={12} className="text-emerald-600" />
            <span>{trajectory.realityCheckStatusLabel}</span>
          </span>
        )
      case 'manageable_commitment':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-teal-50 text-teal-700 border border-teal-200">
            <ShieldCheck size={12} className="text-teal-600" />
            <span>{trajectory.realityCheckStatusLabel}</span>
          </span>
        )
      case 'funding_pressure':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
            <AlertTriangle size={12} className="text-amber-600" />
            <span>{trajectory.realityCheckStatusLabel}</span>
          </span>
        )
      case 'funding_gap':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
            <AlertCircle size={12} className="text-rose-600" />
            <span>{trajectory.realityCheckStatusLabel}</span>
          </span>
        )
      case 'insufficient_information':
      case 'not_started':
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-700 border border-slate-200">
            <HelpCircle size={12} className="text-slate-500" />
            <span>{trajectory.realityCheckStatusLabel}</span>
          </span>
        )
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="petaesok-experience-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200"
    >
      <div className="relative w-full max-w-3xl bg-white rounded-3xl shadow-2xl border border-slate-200/90 overflow-hidden flex flex-col max-h-[92vh] text-left">
        {/* Header Section */}
        <div className="px-5 sm:px-6 py-4 border-b border-slate-100 flex items-start justify-between bg-gradient-to-r from-teal-50/70 via-white to-emerald-50/40">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-teal-100/70 text-[#0F766E] flex items-center justify-center shadow-2xs mt-0.5 flex-shrink-0">
              <Compass size={22} className="text-[#0EA5A4]" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-teal-800 bg-teal-50 px-2 py-0.5 rounded-md border border-teal-200/80">
                  PetaEsok Trajectory
                </span>
                {renderStatusBadge()}
              </div>
              <h2 id="petaesok-experience-title" className="text-base sm:text-lg font-bold text-slate-900 mt-1 leading-snug">
                Peta Perjalanan: {trajectory.goalName}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                <span>Untuk: <strong>{trajectory.personName}</strong></span>
                {trajectory.personRelationship && <span>({trajectory.personRelationship})</span>}
                {trajectory.targetHorizonYear && (
                  <>
                    <span>•</span>
                    <span className="inline-flex items-center gap-1 text-[#0F766E] font-semibold">
                      <Clock size={12} /> Target: Tahun {trajectory.targetHorizonYear} ({trajectory.yearsRemaining} tahun lagi)
                    </span>
                  </>
                )}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer flex-shrink-0"
            aria-label="Tutup PetaEsok"
          >
            <X size={18} />
          </button>
        </div>

        {/* Non-Education Goal Capability Warning (Honest Boundary) */}
        {!trajectory.isEducation && (
          <div className="p-4 sm:p-5 m-5 rounded-2xl bg-amber-50/80 border border-amber-200/90 text-amber-900 space-y-2">
            <div className="flex items-center gap-2 text-xs font-bold text-amber-800">
              <Sparkles size={16} className="text-amber-600 flex-shrink-0" />
              <span>Status Kemampuan Modul PetaEsok</span>
            </div>
            <p className="text-xs text-amber-900 leading-relaxed">
              {trajectory.nonEducationNotice}
            </p>
            <p className="text-[11px] text-amber-800">
              Sasaran Anda tetap tersimpan aman di akun keluarga tanpa perhitungan fiktif. Modul trajektori spesifik untuk kategori ini akan dibuka secara bertahap.
            </p>
          </div>
        )}

        {/* Tab Navigation Menu */}
        {trajectory.isEducation && (
          <div className="flex border-b border-slate-100 px-5 bg-white text-xs font-semibold overflow-x-auto">
            <button
              type="button"
              onClick={() => setActiveTab('journey')}
              className={`py-3 px-3 border-b-2 transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                activeTab === 'journey'
                  ? 'border-[#0EA5A4] text-[#0F766E] font-bold'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <Calendar size={14} />
              <span>1. Linimasa Perjalanan ({trajectory.milestones.length})</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('bekal')}
              className={`py-3 px-3 border-b-2 transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                activeTab === 'bekal'
                  ? 'border-[#0EA5A4] text-[#0F766E] font-bold'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <Wallet size={14} />
              <span>2. Lapisan Bekal & Dana</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('routes')}
              className={`py-3 px-3 border-b-2 transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                activeTab === 'routes'
                  ? 'border-[#0EA5A4] text-[#0F766E] font-bold'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <Route size={14} />
              <span>3. Pilihan Jalur ({trajectory.routes.length})</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('uncertainties')}
              className={`py-3 px-3 border-b-2 transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                activeTab === 'uncertainties'
                  ? 'border-[#0EA5A4] text-[#0F766E] font-bold'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <AlertTriangle size={14} />
              <span>4. Titik Tekanan ({trajectory.pressurePoints.length + trajectory.unknowns.length})</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('actions')}
              className={`py-3 px-3 border-b-2 transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                activeTab === 'actions'
                  ? 'border-[#0EA5A4] text-[#0F766E] font-bold'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <ListTodo size={14} />
              <span>5. Langkah Berikut (P8)</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('whatif')}
              className={`py-3 px-3 border-b-2 transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                activeTab === 'whatif'
                  ? 'border-[#0EA5A4] text-[#0F766E] font-bold'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <SlidersHorizontal size={14} />
              <span>6. Skenario What-If (P9)</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('research')}
              className={`py-3 px-3 border-b-2 transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                activeTab === 'research'
                  ? 'border-[#0EA5A4] text-[#0F766E] font-bold'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <Search size={14} />
              <span>7. Riset & Data Resmi (P11)</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('snapshots')}
              className={`py-3 px-3 border-b-2 transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                activeTab === 'snapshots'
                  ? 'border-[#0EA5A4] text-[#0F766E] font-bold'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              <History size={14} />
              <span>8. Snapshot & Re-Map (P10)</span>
            </button>
          </div>
        )}

        {/* Modal Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-6">
          {/* Section: Where I Am & Where I Want to Go (Context Cards) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            {/* Where I Am */}
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-2">
              <div className="flex items-center gap-1.5 text-slate-700 font-bold uppercase tracking-wider text-[11px]">
                <MapPin size={13} className="text-[#0EA5A4]" />
                <span>Di Mana Saya Sekarang</span>
              </div>
              <p className="text-slate-700 font-medium leading-relaxed">
                {trajectory.currentRealitySummary}
              </p>
              <div className="pt-2 border-t border-slate-200/60 grid grid-cols-2 gap-2 text-[11px]">
                <div>
                  <span className="text-slate-400 block">Kapasitas Bulanan:</span>
                  <span className="font-bold text-slate-800">{trajectory.householdCapacityLabel}</span>
                </div>
                <div>
                  <span className="text-slate-400 block">Tabungan Siap Pakai:</span>
                  <span className="font-bold text-slate-800">{trajectory.availableSavingsLabel}</span>
                </div>
              </div>
            </div>

            {/* Where I Want to Go */}
            <div className="p-4 rounded-2xl bg-teal-50/50 border border-teal-200/70 space-y-2">
              <div className="flex items-center gap-1.5 text-teal-900 font-bold uppercase tracking-wider text-[11px]">
                <Compass size={13} className="text-[#0EA5A4]" />
                <span>Ke Mana Saya Ingin Pergi</span>
              </div>
              <p className="text-teal-950 font-bold leading-relaxed">
                {trajectory.targetSummary}
              </p>
              <div className="pt-2 border-t border-teal-200/50 flex items-center justify-between text-[11px]">
                <span className="text-teal-700">Tahun Horizon Sasaran:</span>
                <span className="font-extrabold text-teal-900">
                  {trajectory.targetHorizonYear ? `Tahun ${trajectory.targetHorizonYear}` : 'Belum ditentukan'}
                </span>
              </div>
            </div>
          </div>

          {/* TAB 1: THE JOURNEY (TIMELINE MILESTONES) */}
          {activeTab === 'journey' && trajectory.isEducation && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                  <Calendar size={14} className="text-[#0EA5A4]" />
                  <span>Linimasa Perjalanan ({trajectory.currentYear} s.d. {trajectory.targetHorizonYear ? trajectory.targetHorizonYear + 4 : 'Mendatang'})</span>
                </h3>
                <span className="text-[11px] text-slate-500 font-medium">
                  {trajectory.yearsRemaining ? `${trajectory.yearsRemaining} tahun masa persiapan` : 'Persiapan awal'}
                </span>
              </div>

              {/* Trajectory Milestone Items */}
              <div className="relative pl-6 space-y-6 before:absolute before:left-2.5 before:top-3 before:bottom-3 before:w-0.5 before:bg-teal-200">
                {trajectory.milestones.map((m) => (
                  <div key={m.id} className="relative group">
                    {/* Milestone Pin Icon */}
                    <div
                      className={`absolute -left-6 top-1 w-5 h-5 rounded-full flex items-center justify-center border-2 bg-white ${
                        m.type === 'current_position'
                          ? 'border-[#0EA5A4] text-[#0EA5A4]'
                          : m.type === 'target_event'
                          ? 'border-emerald-600 text-emerald-600'
                          : m.type === 'funding_checkpoint'
                          ? 'border-teal-500 text-teal-500'
                          : 'border-slate-300 text-slate-400'
                      }`}
                    >
                      <div
                        className={`w-2 h-2 rounded-full ${
                          m.type === 'current_position'
                            ? 'bg-[#0EA5A4]'
                            : m.type === 'target_event'
                            ? 'bg-emerald-600'
                            : 'bg-teal-500'
                        }`}
                      />
                    </div>

                    {/* Milestone Card */}
                    <div className="p-3.5 rounded-2xl bg-white border border-slate-200 shadow-2xs hover:border-teal-200 transition-all space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-extrabold text-slate-900">{m.title}</span>
                        <span className="text-[11px] font-semibold text-teal-800 bg-teal-50 px-2 py-0.5 rounded-full border border-teal-100">
                          {m.dateLabel || `Tahun ${m.year}`}
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed">{m.description}</p>
                      {m.amount && (
                        <div className="pt-2 mt-1 border-t border-slate-100 flex items-center justify-between text-xs">
                          <span className="text-slate-500 text-[11px]">Estimasi Kebutuhan Dana:</span>
                          <span className="font-bold text-slate-900">{formatIDR(m.amount)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Reality Check Connection Banner */}
              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 flex items-start gap-3 text-xs">
                <ShieldCheck size={18} className="text-[#0EA5A4] flex-shrink-0 mt-0.5" />
                <div className="space-y-1 flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-900">{trajectory.realityCheckHeadline}</span>
                    {onOpenRealityCheck && (
                      <button
                        type="button"
                        onClick={() => onOpenRealityCheck(goal?.id)}
                        className="text-[11px] font-semibold text-[#0F766E] hover:underline flex items-center gap-0.5 cursor-pointer"
                      >
                        Buka Detail Reality Check <ArrowRight size={11} />
                      </button>
                    )}
                  </div>
                  <p className="text-slate-600 leading-relaxed text-[11px]">
                    {trajectory.realityCheckSummary}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: BEKAL LAYER */}
          {activeTab === 'bekal' && trajectory.isEducation && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                  <Wallet size={14} className="text-[#0EA5A4]" />
                  <span>Kebutuhan Bekal vs Sumber Pendanaan (Deterministik P5)</span>
                </h3>
              </div>

              {/* Projected Need vs Confirmed Gap */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                <div className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 space-y-1">
                  <span className="text-slate-500 text-[11px] block">Proyeksi Kebutuhan (FV)</span>
                  <span className="text-base font-extrabold text-slate-900 block">
                    {formatIDR(trajectory.bekal.totalProjectedNeed)}
                  </span>
                  <span className="text-[10px] text-slate-400 block">
                    Nominal saat ini: {formatIDR(trajectory.bekal.totalNominalNeed)}
                  </span>
                </div>

                <div className="p-3.5 rounded-2xl bg-emerald-50/50 border border-emerald-200/80 space-y-1">
                  <span className="text-emerald-800 text-[11px] block">Dana Pasti Terkonfirmasi</span>
                  <span className="text-base font-extrabold text-emerald-700 block">
                    {formatIDR(trajectory.bekal.confirmedFundingTotal)}
                  </span>
                  <span className="text-[10px] text-emerald-600 block">
                    {trajectory.bekal.confirmedFundingSources.length} sumber dana pasti
                  </span>
                </div>

                <div className="p-3.5 rounded-2xl bg-teal-50/50 border border-teal-200/80 space-y-1">
                  <span className="text-teal-800 text-[11px] block">Tabungan Bulanan Terencana</span>
                  <span className="text-base font-extrabold text-teal-800 block">
                    {formatIDR(trajectory.bekal.requiredMonthlySavingConfirmed)} / bln
                  </span>
                  <span className="text-[10px] text-teal-600 block">
                    Untuk menutup gap pasti
                  </span>
                </div>
              </div>

              {/* Confirmed vs Estimated Funding Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                {/* Confirmed Funding Box */}
                <div className="p-4 rounded-2xl bg-white border border-emerald-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-emerald-900 flex items-center gap-1.5">
                      <ShieldCheck size={14} className="text-emerald-600" />
                      Sumber Dana Pasti (Kanonikal)
                    </span>
                    <span className="font-extrabold text-emerald-700">
                      {formatIDR(trajectory.bekal.confirmedFundingTotal)}
                    </span>
                  </div>
                  {trajectory.bekal.confirmedFundingSources.length === 0 ? (
                    <p className="text-[11px] text-slate-400 italic">Belum ada sumber dana pasti tercatat.</p>
                  ) : (
                    <ul className="space-y-1.5 text-[11px] text-slate-600">
                      {trajectory.bekal.confirmedFundingSources.map((f) => (
                        <li key={f.id} className="flex items-center justify-between border-b border-slate-100 pb-1">
                          <span>{f.name}</span>
                          <span className="font-semibold text-slate-800">{formatIDR(f.amount || 0)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Estimated Funding Box */}
                <div className="p-4 rounded-2xl bg-white border border-slate-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-800 flex items-center gap-1.5">
                      <TrendingUp size={14} className="text-teal-600" />
                      Potensi Dana / Beasiswa (Estimasi)
                    </span>
                    <span className="font-extrabold text-slate-700">
                      {formatIDR(trajectory.bekal.estimatedFundingTotal)}
                    </span>
                  </div>
                  {trajectory.bekal.estimatedFundingSources.length === 0 ? (
                    <p className="text-[11px] text-slate-400 italic">Belum ada potensi beasiswa/bantuan dicatat.</p>
                  ) : (
                    <ul className="space-y-1.5 text-[11px] text-slate-600">
                      {trajectory.bekal.estimatedFundingSources.map((f) => (
                        <li key={f.id} className="flex items-center justify-between border-b border-slate-100 pb-1">
                          <span>{f.name}</span>
                          <span className="font-semibold text-slate-700">{formatIDR(f.amount || 0)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="text-[10px] text-slate-500 italic pt-1">
                    *Dana estimasi tidak dihitung sebagai dana terkonfirmasi untuk mencegah ilusi kecukupan modal.
                  </p>
                </div>
              </div>

              {/* Action Link to Cost & Funding */}
              {onOpenCostFunding && (
                <div className="pt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={onOpenCostFunding}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#0F766E] hover:underline cursor-pointer"
                  >
                    Perbarui Komponen Biaya & Sumber Dana <ChevronRight size={13} />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* TAB 3: ROUTE OPTIONS (USER CHOOSES) */}
          {activeTab === 'routes' && trajectory.isEducation && (
            <div className="space-y-4">
              <div className="space-y-1">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                  <Route size={14} className="text-[#0EA5A4]" />
                  <span>Pilihan Jalur Perjalanan Terbuka</span>
                </h3>
                <p className="text-xs text-slate-500 leading-relaxed">
                  PetaEsok menyajikan pilihan jalur realistis yang didukung data Anda. Keputusan jalur sepenuhnya berada di tangan Anda tanpa penentuan sepihak oleh sistem.
                </p>
              </div>

              <div className="space-y-3">
                {trajectory.routes.map((r) => {
                  const isSelected = selectedRouteId === r.id
                  return (
                    <div
                      key={r.id}
                      onClick={() => setSelectedRouteId(r.id)}
                      className={`p-4 rounded-2xl border transition-all cursor-pointer ${
                        isSelected
                          ? 'bg-teal-50/40 border-[#0EA5A4] shadow-xs'
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-sm text-slate-900">{r.title}</span>
                          {r.isPrimary && (
                            <span className="text-[10px] font-bold text-teal-800 bg-teal-100/70 px-2 py-0.5 rounded-full">
                              Jalur Utama
                            </span>
                          )}
                        </div>
                        <span
                          className={`text-xs font-semibold px-2.5 py-1 rounded-xl transition-colors ${
                            isSelected ? 'bg-[#0EA5A4] text-white font-bold' : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {isSelected ? 'Jalur Terpilih' : 'Pilih Jalur'}
                        </span>
                      </div>

                      <p className="text-xs text-slate-600 mt-2 leading-relaxed">{r.description}</p>

                      <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5 text-[11px]">
                        <p className="font-semibold text-slate-800">{r.differencesSummary}</p>
                        <div className="space-y-0.5 text-slate-500">
                          {r.tradeOffs.map((to, i) => (
                            <div key={i} className="flex items-center gap-1.5">
                              <span className="w-1 h-1 rounded-full bg-teal-500 flex-shrink-0" />
                              <span>{to}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* TAB 4: PRESSURE POINTS & UNKNOWNS */}
          {activeTab === 'uncertainties' && trajectory.isEducation && (
            <div className="space-y-4">
              {/* Pressure Points */}
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                  <AlertTriangle size={14} className="text-amber-600" />
                  <span>Titik Tekanan Faktual (Berdasarkan Kalkulasi)</span>
                </h4>
                {trajectory.pressurePoints.length === 0 ? (
                  <div className="p-3.5 rounded-2xl bg-emerald-50/60 border border-emerald-200 text-xs text-emerald-900 flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0" />
                    <span>Tidak terdeteksi titik tekanan finansial kritis pada jalur ini.</span>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {trajectory.pressurePoints.map((pp) => (
                      <div key={pp.id} className="p-3.5 rounded-2xl bg-amber-50/50 border border-amber-200/80 space-y-1 text-xs">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-slate-900">{pp.title}</span>
                          <span className="text-[10px] font-semibold text-amber-800 bg-amber-100/70 px-2 py-0.5 rounded-full">
                            {pp.timingLabel}
                          </span>
                        </div>
                        <p className="text-slate-600 leading-relaxed text-[11px]">{pp.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Known Unknowns */}
              <div className="space-y-2 pt-2">
                <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                  <HelpCircle size={14} className="text-slate-500" />
                  <span>Ketidakpastian & Hal yang Belum Diketahui</span>
                </h4>
                {trajectory.unknowns.length === 0 ? (
                  <p className="text-xs text-slate-500 italic">Seluruh komponen biaya dan pendanaan telah memiliki nilai nominal.</p>
                ) : (
                  <div className="space-y-2.5">
                    {trajectory.unknowns.map((u) => (
                      <div key={u.id} className="p-3.5 rounded-2xl bg-slate-50 border border-slate-200 space-y-1 text-xs">
                        <span className="font-bold text-slate-800 block">{u.title}</span>
                        <p className="text-slate-600 text-[11px] leading-relaxed">{u.description}</p>
                        <p className="text-[10px] text-teal-800 font-medium pt-1">Dampak: {u.impact}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 5: P8 ACTION AUTHORITY (NEXT STEPS) */}
          {activeTab === 'actions' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                  <ListTodo size={14} className="text-[#0EA5A4]" />
                  <span>Langkah Tindak Lanjut Terarah (P8)</span>
                </h3>
                <span className="text-[11px] text-slate-500 font-medium">Berdasarkan celah data & sinyal Reality Check</span>
              </div>

              {(() => {
                const actions = deriveTrajectoryActions(trajectory)
                if (actions.length === 0) {
                  return (
                    <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-xs text-emerald-900 flex items-center gap-2">
                      <CheckCircle2 size={16} className="text-emerald-600" />
                      <span>Semua langkah persiapan saat ini dalam status lengkap dan siap jalan.</span>
                    </div>
                  )
                }

                return (
                  <div className="space-y-3">
                    {actions.map((act) => {
                      const isDone = completedActionIds.includes(act.id)
                      return (
                        <div
                          key={act.id}
                          className={`p-4 rounded-2xl border transition-all text-xs ${
                            isDone
                              ? 'bg-slate-50/70 border-slate-200 opacity-60'
                              : act.priority === 'urgent'
                              ? 'bg-rose-50/40 border-rose-200'
                              : act.priority === 'important'
                              ? 'bg-amber-50/30 border-amber-200'
                              : 'bg-white border-slate-200'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="space-y-1 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`font-bold ${isDone ? 'line-through text-slate-500' : 'text-slate-900'}`}>
                                  {act.title}
                                </span>
                                <span
                                  className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                                    act.priority === 'urgent'
                                      ? 'bg-rose-100 text-rose-800'
                                      : act.priority === 'important'
                                      ? 'bg-amber-100 text-amber-800'
                                      : 'bg-teal-50 text-teal-800'
                                  }`}
                                >
                                  {act.priority === 'urgent' ? 'Mendesak' : act.priority === 'important' ? 'Penting' : 'Saran'}
                                </span>
                              </div>
                              <p className="text-slate-600 text-[11px] leading-relaxed">{act.description}</p>
                              <p className="text-[10px] text-slate-400 font-medium">Alasan: {act.sourceReason}</p>
                            </div>

                            <button
                              type="button"
                              onClick={() => {
                                if (isDone) {
                                  setCompletedActionIds(completedActionIds.filter((id) => id !== act.id))
                                } else {
                                  setCompletedActionIds([...completedActionIds, act.id])
                                }
                              }}
                              className={`p-2 rounded-xl border transition-all flex items-center justify-center cursor-pointer ${
                                isDone
                                  ? 'bg-emerald-600 text-white border-emerald-600'
                                  : 'bg-white border-slate-200 hover:border-slate-300 text-slate-400 hover:text-slate-600'
                              }`}
                              title={isDone ? 'Tandai belum selesai' : 'Tandai selesai'}
                            >
                              <Check size={14} />
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )
              })()}
            </div>
          )}

          {/* TAB 6: P9 WHAT-IF SCENARIO AUTHORITY */}
          {activeTab === 'whatif' && (
            <div className="space-y-4 text-xs">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                  <SlidersHorizontal size={14} className="text-[#0EA5A4]" />
                  <span>Simulasi Skenario & What-If (P9)</span>
                </h3>
                <span className="text-[11px] text-teal-700 font-medium">Data dasar Anda tetap aman tanpa perubahan</span>
              </div>

              {/* What-If Interactive Controls */}
              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-4">
                <p className="text-[11px] text-slate-600 leading-relaxed">
                  Uji konsekuensi jika target waktu bergeser, terdapat peluang beasiswa baru, atau alokasi tabungan bulanan ditingkatkan.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">
                      Penyesuaian Biaya Kuliah (Beasiswa / Keringanan):
                    </label>
                    <select
                      value={whatIfModifier.tuitionAdjustmentPercent || 0}
                      onChange={(e) =>
                        setWhatIfModifier({ ...whatIfModifier, tuitionAdjustmentPercent: Number(e.target.value) })
                      }
                      className="w-full px-3 py-2 rounded-xl bg-white border border-slate-200 text-slate-800 text-xs font-semibold focus:outline-[#0EA5A4]"
                    >
                      <option value="0">Sesuai Perkiraan Awal (0%)</option>
                      <option value="-25">Diskon / Beasiswa Parsial (-25%)</option>
                      <option value="-50">Beasiswa Separuh Biaya (-50%)</option>
                      <option value="-100">Beasiswa Kuliah Penuh (-100%)</option>
                      <option value="20">Kenaikan Inflasi Ekstra (+20%)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 mb-1">
                      Tambahan Pendanaan / Beasiswa Satu Kali (Rp):
                    </label>
                    <input
                      type="number"
                      step="5000000"
                      min="0"
                      value={whatIfModifier.additionalOneTimeFunding || ''}
                      onChange={(e) =>
                        setWhatIfModifier({ ...whatIfModifier, additionalOneTimeFunding: Number(e.target.value) || 0 })
                      }
                      placeholder="Contoh: 50000000"
                      className="w-full px-3 py-2 rounded-xl bg-white border border-slate-200 text-slate-800 text-xs font-semibold focus:outline-[#0EA5A4]"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      const res = simulateWhatIfScenario(goal, journey, whatIfModifier)
                      setWhatIfResult(res)
                    }}
                    className="px-4 py-2 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-bold text-xs transition-all shadow-sm cursor-pointer"
                  >
                    Jalankan Simulasi Skenario
                  </button>

                  {whatIfResult && (
                    <button
                      type="button"
                      onClick={() => {
                        setWhatIfModifier({
                          tuitionAdjustmentPercent: 0,
                          livingCostAdjustmentPercent: 0,
                          additionalMonthlyContribution: 0,
                          additionalOneTimeFunding: 0,
                        })
                        setWhatIfResult(null)
                      }}
                      className="px-3 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 text-xs font-semibold transition-all cursor-pointer flex items-center gap-1"
                    >
                      <RotateCcw size={12} />
                      <span>Reset</span>
                    </button>
                  )}
                </div>
              </div>

              {/* What-If Comparison Cards */}
              {whatIfResult && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 animate-in fade-in duration-150">
                  <div className="p-4 rounded-2xl bg-white border border-slate-200 space-y-2">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Kondisi Dasar (Baseline)</span>
                    <div>
                      <span className="text-slate-500 text-[11px] block">Kebutuhan Masa Depan:</span>
                      <span className="text-sm font-bold text-slate-800">{formatIDR(whatIfResult.baseline.futureTotalCost)}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 text-[11px] block">Kebutuhan Tabungan Bulanan:</span>
                      <span className="text-sm font-bold text-slate-800">{formatIDR(whatIfResult.baseline.requiredMonthlySaving)}/bln</span>
                    </div>
                    <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[11px]">
                      <span className="text-slate-500">Status Kelayakan:</span>
                      <span className="font-bold text-slate-700">{whatIfResult.baseline.realityLabel}</span>
                    </div>
                  </div>

                  <div className="p-4 rounded-2xl bg-teal-50/50 border border-teal-200 space-y-2">
                    <span className="text-[10px] font-bold text-teal-800 uppercase tracking-wider block">Hasil Skenario (What-If)</span>
                    <div>
                      <span className="text-teal-700 text-[11px] block">Kebutuhan Masa Depan:</span>
                      <span className="text-sm font-extrabold text-teal-950">{formatIDR(whatIfResult.scenario.futureTotalCost)}</span>
                    </div>
                    <div>
                      <span className="text-teal-700 text-[11px] block">Kebutuhan Tabungan Bulanan:</span>
                      <span className="text-sm font-extrabold text-teal-950">{formatIDR(whatIfResult.scenario.requiredMonthlySaving)}/bln</span>
                    </div>
                    <div className="pt-2 border-t border-teal-200/50 flex items-center justify-between text-[11px]">
                      <span className="text-teal-800">Status Kelayakan:</span>
                      <span className="font-bold text-teal-900">{whatIfResult.scenario.realityLabel}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 7: P11 RESEARCH & AI ORCHESTRATION */}
          {activeTab === 'research' && (
            <div className="space-y-4 text-xs">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                  <Search size={14} className="text-[#0EA5A4]" />
                  <span>Riset Sumber Resmi & Data Terbuka (P11)</span>
                </h3>
                <span className="text-[11px] text-slate-500 font-medium">Transparansi sumber & penelusuran bukti</span>
              </div>

              {/* Research Trigger Box */}
              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                <p className="text-[11px] text-slate-600 leading-relaxed">
                  Lakukan penelusuran data biaya kuliah, estimasi biaya hidup, dan skema beasiswa untuk institusi target:
                  <strong className="text-slate-900 block mt-0.5">
                    {trajectory.targetInstitutionOrProgram || goal.name} (Tahun {trajectory.targetHorizonYear || 2030})
                  </strong>
                </p>

                {/* Mode Selector for Testing Robustness / Failure Modes */}
                <div className="flex items-center gap-2 pt-1 flex-wrap">
                  <label className="text-[11px] font-bold text-slate-700">Simulasi Mode Riset:</label>
                  <select
                    value={researchMode}
                    onChange={(e) => setResearchMode(e.target.value as any)}
                    className="px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-xs font-medium focus:outline-[#0EA5A4]"
                  >
                    <option value="normal">Standar (Temukan Sumber Resmi)</option>
                    <option value="conflict">Uji Konflik Sumber (Beda Angka)</option>
                    <option value="fail">Uji Kegagalan Sistem (AI Offline)</option>
                  </select>
                </div>

                <button
                  type="button"
                  disabled={isResearchLoading}
                  onClick={async () => {
                    setIsResearchLoading(true)
                    try {
                      const res = await executeResearchQuery(
                        {
                          goalId: goal.id,
                          targetInstitution: trajectory.targetInstitutionOrProgram || goal.name,
                          targetCountry: trajectory.targetSummary.includes('Hong Kong') ? 'Hong Kong' : 'Indonesia',
                          targetYear: trajectory.targetHorizonYear,
                        },
                        {
                          mockFail: researchMode === 'fail',
                          mockConflict: researchMode === 'conflict',
                        }
                      )
                      setResearchState(res)
                    } finally {
                      setIsResearchLoading(false)
                    }
                  }}
                  className="px-4 py-2 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-bold text-xs transition-all shadow-sm cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
                >
                  <Search size={13} />
                  <span>{isResearchLoading ? 'Menghubungkan & Menelusuri...' : 'Mulai Riset Bukti Resmi'}</span>
                </button>
              </div>

              {/* Research Results Display */}
              {researchState && (
                <div className="space-y-3 animate-in fade-in duration-150">
                  <div
                    className={`p-3.5 rounded-2xl border text-xs flex items-center justify-between ${
                      researchState.status === 'success'
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-950'
                        : researchState.status === 'conflicting_sources'
                        ? 'bg-amber-50 border-amber-200 text-amber-950'
                        : 'bg-rose-50 border-rose-200 text-rose-950'
                    }`}
                  >
                    <span className="font-semibold">{researchState.statusMessage}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/70">
                      {researchState.status}
                    </span>
                  </div>

                  {researchState.candidates.map((cand) => (
                    <div key={cand.id} className="p-4 rounded-2xl bg-white border border-slate-200 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-slate-900 text-xs">{cand.title}</span>
                        {cand.amount && (
                          <span className="font-extrabold text-[#0F766E]">
                            {cand.currency} {cand.amount.toLocaleString('id-ID')}
                            {cand.period ? ` / ${cand.period}` : ''}
                          </span>
                        )}
                      </div>

                      <p className="text-slate-600 text-[11px] leading-relaxed italic bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                        "{cand.evidenceText}"
                      </p>

                      <div className="pt-2 border-t border-slate-100 grid grid-cols-2 gap-2 text-[10px] text-slate-500">
                        <div>
                          <span className="font-bold text-slate-700 block">Sumber Resmi:</span>
                          <span>{cand.provenance.sourceName}</span>
                        </div>
                        <div>
                          <span className="font-bold text-slate-700 block">Tingkat Keyakinan:</span>
                          <span className="capitalize">{cand.provenance.confidence} (Diverifikasi {cand.provenance.lastVerifiedAt.slice(0, 10)})</span>
                        </div>
                      </div>

                      {/* Human Confirmation Gate */}
                      <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-2">
                        <span className="text-[10px] font-medium text-slate-500">
                          Status: <span className="font-bold uppercase text-slate-700">{cand.status}</span>
                        </span>
                        <div className="flex items-center gap-1.5">
                          {cand.status === 'candidate' ? (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  rejectResearchCandidate(goal.id, cand.id)
                                  setResearchState({
                                    ...researchState,
                                    candidates: researchState.candidates.map((c) =>
                                      c.id === cand.id ? { ...c, status: 'rejected' } : c
                                    ),
                                  })
                                }}
                                className="px-2.5 py-1 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 text-[11px] font-semibold transition-all cursor-pointer"
                              >
                                Tolak
                              </button>
                              <button
                                type="button"
                                onClick={async () => {
                                  await acceptResearchCandidate(goal.id, cand)
                                  setResearchState({
                                    ...researchState,
                                    candidates: researchState.candidates.map((c) =>
                                      c.id === cand.id ? { ...c, status: 'accepted' } : c
                                    ),
                                  })
                                }}
                                className="px-3 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold transition-all shadow-2xs cursor-pointer flex items-center gap-1"
                              >
                                <CheckCircle2 size={12} />
                                <span>Konfirmasi & Masukkan ke Rencana</span>
                              </button>
                            </>
                          ) : cand.status === 'accepted' ? (
                            <span className="text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md text-[10px] font-bold flex items-center gap-1">
                              <CheckCircle2 size={11} /> Diterima ke Data Perencanaan (P5)
                            </span>
                          ) : (
                            <span className="text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md text-[10px] font-medium">
                              Ditolak oleh Pengguna
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 8: P10 SNAPSHOT & RE-MAP */}
          {activeTab === 'snapshots' && (
            <div className="space-y-4 text-xs">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                  <History size={14} className="text-[#0EA5A4]" />
                  <span>Riwayat Snapshot & Re-Map (P10)</span>
                </h3>
                <span className="text-[11px] text-slate-500 font-medium">Jejak perbandingan ketika hidup berubah</span>
              </div>

              {/* Create Snapshot Trigger */}
              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-between gap-3">
                <div className="space-y-0.5">
                  <span className="font-bold text-slate-800 block">Bekukan Snapshot Saat Ini</span>
                  <p className="text-[11px] text-slate-500">Simpan versi perencanaan aktif sebagai titik pembanding sebelum ada perubahan hidup.</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const snap = createPlanSnapshot(
                      goal,
                      trajectory,
                      deriveTrajectoryActions(trajectory),
                      'manual_checkpoint',
                      `Snapshot Perencanaan ${new Date().toLocaleDateString('id-ID')}`
                    )
                    setSnapshots(getPlanSnapshots(goal.id))
                    setSnapshotSuccessNote(`Snapshot Versi ${snap.snapshotVersion} berhasil disimpan!`)
                    setTimeout(() => setSnapshotSuccessNote(null), 3500)
                  }}
                  className="px-4 py-2 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-bold text-xs transition-all shadow-sm cursor-pointer whitespace-nowrap"
                >
                  Simpan Snapshot
                </button>
              </div>

              {snapshotSuccessNote && (
                <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs font-semibold animate-in fade-in">
                  {snapshotSuccessNote}
                </div>
              )}

              {/* Stored Snapshots List */}
              <div className="space-y-2.5">
                <span className="text-[11px] font-bold text-slate-700 block">Daftar Versi Tersimpan ({snapshots.length}):</span>
                {snapshots.length === 0 ? (
                  <p className="text-slate-500 italic text-[11px]">Belum ada snapshot historis tersimpan untuk sasaran ini.</p>
                ) : (
                  snapshots.map((s) => (
                    <div key={s.id} className="p-3.5 rounded-2xl bg-white border border-slate-200 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-slate-900">Versi {s.snapshotVersion}: {s.triggerNote}</span>
                        <span className="text-[10px] text-slate-400 font-medium">{s.createdAt.slice(0, 10)}</span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1 border-t border-slate-100 text-[10px] text-slate-600">
                        <div>
                          <span className="text-slate-400 block">Target Masa Depan:</span>
                          <span className="font-bold text-slate-800">{formatIDR(s.summary.totalFutureCost)}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 block">Tabungan Bulanan:</span>
                          <span className="font-bold text-slate-800">{formatIDR(s.summary.requiredMonthlySaving)}/bln</span>
                        </div>
                        <div>
                          <span className="text-slate-400 block">Dana Terkonfirmasi:</span>
                          <span className="font-bold text-slate-800">{formatIDR(s.summary.confirmedFunding)}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 block">Tahun Target:</span>
                          <span className="font-bold text-slate-800">{s.summary.targetYear || '-'}</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer / Navigation Controls */}
        <div className="p-4 border-t border-slate-100 bg-slate-50/70 flex items-center justify-between gap-3">
          <div className="text-[11px] text-slate-500 hidden sm:block">
            PetaEsok Core Trajectory • BekalNanti.ID
          </div>

          <div className="flex items-center gap-2 ml-auto">
            {onOpenCalculation && trajectory.isEducation && (
              <button
                type="button"
                onClick={() => onOpenCalculation(goal?.id)}
                className="px-3.5 py-2 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-100 text-xs font-semibold transition-colors cursor-pointer"
              >
                Lihat Perhitungan (P5)
              </button>
            )}

            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white text-xs font-bold transition-all shadow-sm cursor-pointer"
            >
              Kembali ke Rencana
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
